import { FolderId } from 'modules/domains/foldering';
import { TemplateId } from 'modules/domains/template';
import { deleteRequest, get, post } from 'services/api';

export const EDIT_TEMPLATE_NAME_URL = '/templates/editName';
export const COPY_TEMPLATE_URL = '/templates/clone';
export const COPY_TEMPLATE_TO_PROJECT_URL = '/i/templates/cloneToProject';
export const POST_REMOVE_LOCALE = '/i/templates/removeLocale';
export const DELETE_TEMPLATE_URL = '/templates/delete';
export const DELETE_TEMPLATES_BULK_URL = '/templates/deleteBulk';
export const EXPORT_TEMPLATE_HTML_URL = (id: string) => `/templates/${id}/export`;
export const MESSAGE_TYPES_AND_SENDERS_URL = '/i/settings/messageTypesAndSenders';
export const TEMPLATE_PREVIEW_THUMBNAILS_URL = '/templates/getPreviewThumbnails';

/**
 * Copies a template, if given a valid folder copies it to that folder
 */
export const copyTemplate = (
  srcTemplateId: TemplateId,
  destinationFolderId?: FolderId,
  defaultLocaleToClone?: string,
  secondaryLocalesToClone?: string[],
) => {
  const params = new URLSearchParams(`templateId=${srcTemplateId}`);
  if (destinationFolderId) {
    params.set('destinationFolderId', destinationFolderId.toString());
  }
  if (defaultLocaleToClone?.length) {
    params.set('defaultLocaleToClone', defaultLocaleToClone);
  }
  if (secondaryLocalesToClone?.length) {
    // BE receives an array as ?secondaryLocalesToClone=en-US&secondaryLocalesToClone=fr-FR
    // not ?secondaryLocalesToClone=en-US,fr-FR
    secondaryLocalesToClone.forEach((locale) => {
      params.append('secondaryLocalesToClone', locale);
    });
  }
  return get(`${COPY_TEMPLATE_URL}?${params.toString()}`);
};

type CopyTemplateAPIProps = {
  srcTemplateId: number;
  destProjectId: number;
  destMessageTypeId: number;
  destEmailSender: string;
  destSmsSenderId: number;
  defaultLocaleToClone: string;
  secondaryLocalesToClone: string[];
};

export const copyTemplateToProject = (copyTemplateAPIProps: CopyTemplateAPIProps) => {
  const { defaultLocaleToClone, secondaryLocalesToClone, ...propsWithoutLocaleInfo } =
    copyTemplateAPIProps;
  if (defaultLocaleToClone?.length) {
    return post(COPY_TEMPLATE_TO_PROJECT_URL, {
      defaultLocaleToClone,
      secondaryLocalesToClone,
      ...propsWithoutLocaleInfo,
    });
  } else {
    return post(COPY_TEMPLATE_TO_PROJECT_URL, propsWithoutLocaleInfo);
  }
};

export const editTemplateName = (templateId: number, name: string) =>
  post(EDIT_TEMPLATE_NAME_URL, { templateId, name });

export const getMessageTypesAndSenders = (projectId: number, messageMedium: string) =>
  get(`${MESSAGE_TYPES_AND_SENDERS_URL}?projectId=${projectId}&messageMedium=${messageMedium}`);

export const deleteTemplate = (templateId: number) =>
  post(`${DELETE_TEMPLATE_URL}?templateId=${templateId}`);

// this serializes ids into the url eg /templates/deleteBulk?templateIdsToDelete=1&templateIdsToDelete=2 to pass them in as an array
export const deleteTemplates = (templateIdsToDelete: TemplateId[]) =>
  deleteRequest(
    `${DELETE_TEMPLATES_BULK_URL}${templateIdsToDelete
      .map((templateId, idx) => `${idx !== 0 ? '&' : '?'}templateIdsToDelete=${templateId}`)
      .join('')}`,
  );

export const getTemplatePreviewThumbnails = (templateIds: TemplateId[]) =>
  get(
    `${TEMPLATE_PREVIEW_THUMBNAILS_URL}${templateIds
      .map((templateId, idx) => `${idx !== 0 ? '&' : '?'}templateIds=${templateId}`)
      .join('')}`,
  );
export const exportTemplateHTML = (templateId: number) =>
  get(`${EXPORT_TEMPLATE_HTML_URL(templateId.toString())}?templateId=${templateId}`);

export const removeLocale = (templateId: number, locale: string) => {
  return post(POST_REMOVE_LOCALE, { templateId, locale });
};
